'use client';

import { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { ArrowUpRight, CircleAlert } from 'lucide-react';
import {
  GsImage,
  ImageBackdropLayers,
  cn,
} from '@/components/help-center/latest-power-shared';
import {
  HOME_EXPLORE_ACE_SMILIES,
  HOME_EXPLORE_HUB_CARDS,
  type HomeExploreHubCard,
  type HomeExploreHubCardId,
} from '@/lib/home-explore-hub';
import { fetchRandomSmilieCombos } from '@/lib/random-smilies';
import { smilieOrPawnImageCandidates } from '@/lib/xat-smilie';

/** Fallbacks com efeitos — enquanto a API carrega ou se falhar. */
const COMBO_FALLBACK = [
  'smile#r',
  'cool#g',
  'wink#b',
  'biggrin#y',
  'tongue#m',
] as const;

/**
 * Smilie do card combo — proxy same-origin + fallbacks (evita ORB no CDN).
 * Só revela no onLoad para evitar flicker da cascata de candidatos.
 */
function ComboHubSmiley({
  code,
  className,
  imgClassName,
}: {
  code: string;
  className?: string;
  imgClassName?: string;
}) {
  const candidates = smilieOrPawnImageCandidates(code, 40);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setCandidateIndex(0);
    setVisible(false);
  }, [code]);

  const src = candidates[candidateIndex];
  if (!src) return null;

  return (
    <div
      className={cn(
        className,
        'transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      <img
        src={src}
        alt=""
        className={imgClassName}
        decoding="async"
        draggable={false}
        onLoad={() => setVisible(true)}
        onError={() => {
          if (candidateIndex + 1 < candidates.length) {
            setCandidateIndex((i) => i + 1);
          }
        }}
      />
    </div>
  );
}

/** Fila L→R: sobrepostos, crescendo até o botevery (destaque final). */
const BOT_STACK = [
  { src: '/icons/botpink.webp', size: 108, duration: 4.6, delay: 0.05, tilt: -7 },
  { src: '/icons/botblue.webp', size: 118, duration: 5.0, delay: 0.2, tilt: -4 },
  { src: '/icons/botpurple.webp', size: 128, duration: 4.8, delay: 0.35, tilt: -2 },
  { src: '/icons/botgold.webp', size: 140, duration: 5.1, delay: 0.15, tilt: 2 },
  { src: '/icons/botruby.webp', size: 152, duration: 4.9, delay: 0.45, tilt: 4 },
  { src: '/icons/botevery.webp', size: 176, duration: 4.7, delay: 0.1, tilt: 0 },
] as const;

const cardShellBase = cn(
  'group relative flex h-full min-h-[11.5rem] rounded-2xl',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-28px_rgba(0,0,0,0.75)]',
  'backdrop-blur-sm outline-none transition-all duration-300',
  'hover:-translate-y-1',
  'focus-visible:ring-2'
);

/** Tom próprio por card — mesmo estilo, identidade diferente. */
const cardTheme: Record<
  HomeExploreHubCardId,
  { shell: string; wash: string; cta: string; focus: string }
> = {
  combo: {
    shell:
      'border border-violet-400/25 bg-gradient-to-br from-violet-400/[0.14] via-fuchsia-500/[0.04] to-transparent hover:border-violet-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(167,139,250,0.18),0_22px_50px_-20px_rgba(139,92,246,0.4)]',
    wash: 'from-violet-950/50 via-transparent to-[#1a0530]/45',
    cta: 'text-violet-300/95 group-hover:text-violet-200',
    focus: 'focus-visible:ring-violet-400/50',
  },
  bot: {
    shell:
      'border border-emerald-400/25 bg-gradient-to-br from-emerald-400/[0.12] via-teal-500/[0.04] to-transparent hover:border-emerald-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(52,211,153,0.16),0_22px_50px_-20px_rgba(16,185,129,0.4)]',
    wash: 'from-emerald-950/45 via-transparent to-[#031a14]/50',
    cta: 'text-emerald-300/95 group-hover:text-emerald-200',
    focus: 'focus-visible:ring-emerald-400/50',
  },
  aces: {
    shell:
      'border border-amber-400/25 bg-gradient-to-br from-amber-400/[0.14] via-yellow-500/[0.04] to-transparent hover:border-amber-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(251,191,36,0.18),0_22px_50px_-20px_rgba(245,158,11,0.4)]',
    wash: 'from-amber-950/40 via-transparent to-[#1a1003]/50',
    cta: 'text-amber-300/95 group-hover:text-amber-200',
    focus: 'focus-visible:ring-amber-400/50',
  },
  erros: {
    shell:
      'border border-rose-400/25 bg-gradient-to-br from-rose-500/[0.14] via-red-500/[0.04] to-transparent hover:border-rose-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(251,113,133,0.18),0_22px_50px_-20px_rgba(244,63,94,0.4)]',
    wash: 'from-rose-950/45 via-transparent to-[#1a0508]/50',
    cta: 'text-rose-300/95 group-hover:text-rose-200',
    focus: 'focus-visible:ring-rose-400/50',
  },
  tickets: {
    shell:
      'border border-sky-400/25 bg-gradient-to-br from-sky-400/[0.14] via-blue-500/[0.04] to-transparent hover:border-sky-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(56,189,248,0.16),0_22px_50px_-20px_rgba(14,165,233,0.4)]',
    wash: 'from-[#071238]/50 via-[#071238]/25 to-[#030023]/55',
    cta: 'text-sky-300/95 group-hover:text-sky-200',
    focus: 'focus-visible:ring-sky-400/50',
  },
  fairtrade: {
    shell:
      'border border-yellow-400/25 bg-gradient-to-br from-yellow-400/[0.12] via-orange-400/[0.04] to-transparent hover:border-yellow-300/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(250,204,21,0.16),0_22px_50px_-20px_rgba(234,179,8,0.35)]',
    wash: 'from-yellow-950/35 via-transparent to-[#1a1203]/50',
    cta: 'text-yellow-300/95 group-hover:text-yellow-200',
    focus: 'focus-visible:ring-yellow-400/50',
  },
};

const mosaicPlacement: Record<HomeExploreHubCardId, string> = {
  combo: 'md:col-span-3 md:row-span-2 lg:col-span-4 lg:row-span-2 lg:min-h-[22rem]',
  bot: 'md:col-span-3 min-h-[8.75rem] lg:col-span-8 lg:min-h-[9.25rem]',
  aces: 'md:col-span-2 lg:col-span-4',
  erros: 'md:col-span-2 lg:col-span-4',
  tickets: 'md:col-span-3 lg:col-span-7',
  fairtrade: 'md:col-span-3 min-h-[8.75rem] lg:col-span-5 lg:min-h-[9.25rem]',
};

/** Ícone do Fairtrade — gira no hover; ao sair, volta suavemente à posição original. */
function BleedIcon({ src, spinning = false }: { src: string; spinning?: boolean }) {
  const controls = useAnimationControls();

  useEffect(() => {
    if (spinning) {
      void controls.start({
        rotate: 360,
        transition: { duration: 14, ease: 'linear', repeat: Infinity },
      });
      return;
    }
    void controls.start({
      rotate: 0,
      transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    });
  }, [spinning, controls]);

  return (
    <div
      className="pointer-events-none relative -mt-6 mb-0 mr-1 flex h-[10rem] w-[10rem] shrink-0 items-center justify-center self-center sm:-mt-7 sm:mr-2 sm:h-[11.5rem] sm:w-[11.5rem]"
      aria-hidden
    >
      <motion.img
        src={src}
        alt=""
        className="h-full w-full object-contain drop-shadow-[0_10px_24px_rgba(0,0,0,0.4)]"
        initial={{ rotate: 0 }}
        animate={controls}
        draggable={false}
      />
    </div>
  );
}

/** Bots em fila L→R no canto direito; botevery por último e em destaque. */
function BotIconsCluster() {
  return (
    <div
      className="pointer-events-none absolute bottom-0 right-0 z-[1] flex h-[12rem] items-end justify-end overflow-visible pr-0 sm:h-[13.5rem]"
      aria-hidden
    >
      <div className="relative flex items-end justify-end">
        {BOT_STACK.map((item, index) => (
          <motion.div
            key={item.src}
            className={cn(
              'relative shrink-0',
              index > 0 && (index === BOT_STACK.length - 1 ? '-ml-14 sm:-ml-16' : '-ml-12 sm:-ml-14')
            )}
            style={{
              zIndex: index + 1,
              width: item.size,
              height: item.size,
            }}
            animate={{ y: [0, -7, 0], rotate: [item.tilt, item.tilt + 2, item.tilt] }}
            transition={{
              duration: item.duration,
              delay: item.delay,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          >
            <img
              src={item.src}
              alt=""
              className={cn(
                'h-full w-full object-contain drop-shadow-[0_12px_24px_rgba(3,7,25,0.85)]',
                index === BOT_STACK.length - 1 &&
                  'brightness-110 drop-shadow-[0_14px_32px_rgba(16,185,129,0.5)]'
              )}
              draggable={false}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function CardArt({
  card,
  comboCodes,
  spinning,
}: {
  card: HomeExploreHubCard;
  comboCodes?: string[] | null;
  spinning?: boolean;
}) {
  const [a, b] = card.smilies;

  if (card.id === 'combo') {
    const codes = (comboCodes?.length ? comboCodes : [...COMBO_FALLBACK]).slice(0, 5);
    const slots = [
      {
        className:
          'absolute left-[8%] top-[8%] h-14 w-14 rotate-[-14deg] transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-20deg] sm:left-[12%] sm:h-16 sm:w-16',
        imgClassName: 'h-full w-full object-contain drop-shadow-[0_8px_18px_rgba(0,0,0,0.45)]',
      },
      {
        className:
          'absolute right-[6%] top-[4%] h-16 w-16 rotate-[10deg] transition-transform duration-500 group-hover:translate-x-1 group-hover:rotate-[16deg] sm:right-[10%] sm:h-[4.5rem] sm:w-[4.5rem]',
        imgClassName: 'h-full w-full object-contain drop-shadow-[0_8px_18px_rgba(0,0,0,0.45)]',
      },
      {
        className:
          'absolute left-1/2 top-[2%] h-12 w-12 -translate-x-1/2 transition-transform duration-500 group-hover:-translate-y-2 sm:h-14 sm:w-14',
        imgClassName: 'h-full w-full object-contain opacity-90',
      },
      {
        className:
          'absolute bottom-[6%] left-[18%] h-11 w-11 opacity-85 transition-transform duration-500 group-hover:-translate-x-1 sm:left-[22%] sm:h-12 sm:w-12',
        imgClassName: 'h-full w-full object-contain',
      },
      {
        className:
          'absolute bottom-[4%] right-[16%] h-12 w-12 rotate-[8deg] opacity-90 transition-transform duration-500 group-hover:-translate-y-1 sm:right-[20%] sm:h-14 sm:w-14',
        imgClassName: 'h-full w-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)]',
      },
    ] as const;

    return (
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -right-6 top-8 h-36 w-36 rounded-full bg-violet-400/15 blur-3xl transition-opacity duration-300 group-hover:opacity-100" />
        <div className="absolute inset-x-0 top-0 h-[58%] sm:h-[62%]">
          {codes.map((code, index) => {
            const slot = slots[index];
            if (!slot) return null;
            return (
              <ComboHubSmiley
                key={`${index}-${code}`}
                code={code}
                className={slot.className}
                imgClassName={slot.imgClassName}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (card.id === 'bot') {
    return <BotIconsCluster />;
  }

  if (card.id === 'aces') {
    const codes = card.smilies.length ? card.smilies : HOME_EXPLORE_ACE_SMILIES;
    const yellow = codes.find((c) => c === 'yellow') ?? 'yellow';
    const others = codes.filter((c) => c !== yellow);
    // Flex no canto: extremos | médios | yellow | xaf | iconcolor
    const second = others.slice(0, 2);
    const third = others.slice(2, 4);
    const row: { code: string; tier: 1 | 2 | 3 }[] = [];
    if (third[0]) row.push({ code: third[0], tier: 3 });
    if (second[0]) row.push({ code: second[0], tier: 2 }); // vaf
    row.push({ code: yellow, tier: 1 });
    if (second[1]) row.push({ code: second[1], tier: 2 }); // xaf
    if (third[1]) row.push({ code: third[1], tier: 3 }); // iconcolor

    const yellowIndex = row.findIndex((r) => r.tier === 1);

    const tierSizeClass: Record<1 | 2 | 3, string> = {
      1: 'h-[6.75rem] w-[6.75rem] sm:h-[7.5rem] sm:w-[7.5rem] z-[5]',
      2: 'h-[4.75rem] w-[4.75rem] sm:h-[5.25rem] sm:w-[5.25rem] z-[3]',
      3: 'h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem] opacity-95 z-[1]',
    };

    return (
      <div
        className="pointer-events-none absolute -top-3 right-0 z-[1] flex items-start justify-end overflow-visible sm:-top-4 sm:right-1"
        aria-hidden
      >
        <div className="absolute -right-2 top-0 h-28 w-28 rounded-full bg-amber-300/15 blur-3xl" />
        <div className="relative flex items-end justify-end">
          {row.map(({ code, tier }, index) => {
            const afterYellow = yellowIndex >= 0 && index > yellowIndex;
            const isXaf = code === 'xaf';
            const isMidAce = code === 'vaf' || code === 'xaf';
            const overlap =
              index > 0 &&
              (isXaf
                ? '-ml-11 sm:-ml-12'
                : afterYellow
                  ? '-ml-9 sm:-ml-10'
                  : tier === 1
                    ? '-ml-8 sm:-ml-9'
                    : '-ml-7 sm:-ml-8');

            // Base Y: yellow um pouco alto; vaf/xaf intermediários; 3ª linha mais baixa
            const baseY = tier === 1 ? -10 : isMidAce ? -4 : 8;
            const floatAmp = tier === 1 ? 8 : isMidAce ? 7 : 6;

            return (
              <motion.img
                key={`${code}-${tier}-${index}`}
                src={`/icons/${code}.webp`}
                alt=""
                className={cn(
                  'relative shrink-0 object-contain',
                  'drop-shadow-[0_10px_18px_rgba(3,7,25,0.85)]',
                  tierSizeClass[tier],
                  overlap
                )}
                animate={{ y: [baseY, baseY - floatAmp, baseY] }}
                transition={{
                  duration: 2.6 + index * 0.22,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: 'easeInOut',
                  delay: index * 0.18,
                }}
                draggable={false}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (card.id === 'erros') {
    return (
      <div
        className="pointer-events-none relative mr-1 flex h-full w-[7.5rem] shrink-0 items-center justify-center self-stretch sm:mr-2 sm:w-[8.5rem]"
        aria-hidden
      >
        <motion.div
          className="absolute right-0 top-1/2 h-36 w-36 -translate-y-1/2 rounded-full bg-rose-500/25 blur-3xl"
          animate={{ opacity: [0.35, 0.85, 0.35], scale: [0.9, 1.15, 0.9] }}
          transition={{ duration: 1.7, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
        />
        <motion.div
          className="relative"
          animate={{
            scale: [1, 1.07, 1],
            filter: [
              'drop-shadow(0 0 10px rgba(244,63,94,0.45)) drop-shadow(0 8px 18px rgba(0,0,0,0.4))',
              'drop-shadow(0 0 28px rgba(251,113,133,0.95)) drop-shadow(0 0 48px rgba(244,63,94,0.55))',
              'drop-shadow(0 0 10px rgba(244,63,94,0.45)) drop-shadow(0 8px 18px rgba(0,0,0,0.4))',
            ],
          }}
          transition={{ duration: 1.7, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
        >
          <CircleAlert
            className="h-[6.25rem] w-[6.25rem] text-rose-400 sm:h-[7rem] sm:w-[7rem]"
            strokeWidth={1.5}
            aria-hidden
          />
        </motion.div>
      </div>
    );
  }

  if (card.id === 'tickets') {
    const main = a || 'bugou';
    return (
      <div
        className="pointer-events-none relative flex h-full w-[9.5rem] shrink-0 items-center justify-center self-stretch sm:w-[11rem]"
        aria-hidden
      >
        {/* Preenche o canto direito até a borda (sem margem) */}
        <div className="absolute inset-0 bg-gradient-to-l from-sky-400/[0.16] via-sky-500/[0.06] to-transparent" />
        <div className="relative z-[1] flex items-end justify-center pr-3 sm:pr-4">
          {b ? (
            <GsImage
              code={b}
              className="relative z-[1] mb-2 h-11 w-11 opacity-80 transition-transform duration-500 group-hover:-translate-x-0.5"
              imgClassName="h-full w-full object-contain"
              sizePx={40}
            />
          ) : null}
          <GsImage
            code={main}
            className={cn(
              'relative h-24 w-24 transition-transform duration-500 group-hover:scale-105 sm:h-28 sm:w-28',
              b && '-ml-2'
            )}
            imgClassName="h-full w-full object-contain drop-shadow-[0_12px_24px_rgba(56,189,248,0.2)]"
            sizePx={40}
          />
        </div>
      </div>
    );
  }

  return <BleedIcon src="/icons/trade.svg" spinning={spinning} />;
}

function HubCard({
  card,
  comboCodes,
}: {
  card: HomeExploreHubCard;
  comboCodes?: string[] | null;
}) {
  const [fairtradeHover, setFairtradeHover] = useState(false);
  const allowsBleed = card.id === 'fairtrade';
  /** Arte em overlay no canto (não rouba largura do texto). */
  const isCornerOverlay = card.id === 'bot' || card.id === 'aces';
  /** Texto à esquerda + arte à direita (mesmo alinhamento do Fairtrade). */
  const isSideLayout =
    allowsBleed || card.id === 'erros' || card.id === 'tickets';
  const theme = cardTheme[card.id];
  const isComingSoon = card.id === 'combo';

  const textBlock = (
    <div
      className={cn(
        'relative z-[1] flex min-w-0 flex-col gap-2',
        isCornerOverlay
          ? cn(
              'flex-1 justify-center p-4 sm:p-5',
              card.id === 'bot' && 'max-w-[min(100%,28rem)] pr-[10.5rem] sm:pr-[12.5rem]',
              card.id === 'aces' && 'mt-auto max-w-[min(100%,16rem)] pr-4 pt-[4.25rem] sm:pt-20'
            )
          : isSideLayout
            ? 'flex-1 self-center justify-center p-4 pr-2 sm:p-5 sm:pr-3'
            : 'mt-auto w-full max-w-none p-5 pr-16 sm:p-6 sm:pr-20'
      )}
    >
      <h3 className="font-poppins text-[1.15rem] font-bold leading-tight tracking-tight text-white sm:text-xl">
        {card.title}
      </h3>
      <p
        className={cn(
          'font-inter text-[13px] leading-snug text-blue-100/80 sm:text-sm',
          isSideLayout || isCornerOverlay
            ? 'max-w-[min(100%,26rem)]'
            : 'max-w-[min(100%,28rem)]',
          card.id === 'bot' && 'line-clamp-2 max-w-[min(100%,22rem)]'
        )}
      >
        {card.description}
      </p>
      <span
        className={cn(
          'mt-1 inline-flex items-center gap-1 font-poppins text-[12px] font-semibold transition-colors duration-200',
          theme.cta,
          isComingSoon && 'opacity-80'
        )}
      >
        {isComingSoon ? 'Em breve' : card.cta}
        {!isComingSoon && (
          <ArrowUpRight
            className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={2.5}
            aria-hidden
          />
        )}
      </span>
    </div>
  );

  return (
    <a
      href={isComingSoon ? '#' : card.href}
      className={cn(
        cardShellBase,
        theme.shell,
        theme.focus,
        mosaicPlacement[card.id],
        allowsBleed
          ? 'z-[1] min-h-0 flex-row items-center overflow-visible'
          : isCornerOverlay
            ? 'relative z-[1] overflow-visible'
          : isSideLayout
            ? 'flex-row items-center overflow-hidden'
            : 'flex-col overflow-hidden',
        isComingSoon && 'cursor-default'
      )}
      aria-label={
        isComingSoon
          ? `${card.title}. Em breve. ${card.description}`
          : `${card.title}. ${card.description}`
      }
      aria-disabled={isComingSoon || undefined}
      onClick={isComingSoon ? (e) => e.preventDefault() : undefined}
      onMouseEnter={() => {
        if (card.id === 'fairtrade') setFairtradeHover(true);
      }}
      onMouseLeave={() => {
        if (card.id === 'fairtrade') setFairtradeHover(false);
      }}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br',
          theme.wash
        )}
        aria-hidden
      />

      {isSideLayout || isCornerOverlay ? (
        <>
          {isCornerOverlay && (
            <CardArt card={card} spinning={undefined} />
          )}
          {textBlock}
          {isSideLayout && (
            <CardArt card={card} spinning={card.id === 'fairtrade' ? fairtradeHover : undefined} />
          )}
        </>
      ) : (
        <>
          <CardArt card={card} comboCodes={card.id === 'combo' ? comboCodes : undefined} />
          {textBlock}
        </>
      )}
    </a>
  );
}

/** Hub visual em mosaico — destinos especiais da homepage (abaixo da Powerpedia). */
export default function HomeExploreHub() {
  const [comboCodes, setComboCodes] = useState<string[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchRandomSmilieCombos(5, controller.signal).then((codes) => {
      if (controller.signal.aborted) return;
      setComboCodes(codes.length > 0 ? codes : [...COMBO_FALLBACK]);
    });
    return () => controller.abort();
  }, []);

  return (
    <section
      className="relative overflow-x-clip overflow-y-visible px-6 py-16 sm:py-20"
      aria-labelledby="home-explore-hub-heading"
    >
      <ImageBackdropLayers className="absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 bg-slate-950/60" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent"
        aria-hidden
      />

      <div className="relative z-10 mx-auto max-w-7xl">
        <header className="mb-8 max-w-2xl sm:mb-10">
          <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-sky-400/25 bg-sky-500/[0.08] px-3.5 py-1.5 font-poppins text-[11px] font-semibold uppercase tracking-wide text-sky-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm">
            Destinos
          </span>
          <h2
            id="home-explore-hub-heading"
            className="font-poppins text-[clamp(1.65rem,3.5vw,2.35rem)] font-bold leading-[1.15] tracking-tight text-white"
          >
            Descubra novos {' '}
            <span className="bg-gradient-to-r from-sky-300 via-cyan-300 to-sky-200 bg-clip-text text-transparent">
              caminhos
            </span>
          </h2>
          <p className="mt-3 max-w-xl font-poppins text-[15px] leading-relaxed text-blue-100/75">
          Conheça recursos especiais, ferramentas úteis e conteúdos feitos para a comunidade.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-3 sm:gap-3.5 md:grid-cols-6 lg:grid-cols-12 lg:grid-rows-[minmax(9.25rem,auto)_minmax(10.5rem,auto)_minmax(9.25rem,auto)]">
          {HOME_EXPLORE_HUB_CARDS.map((card) => (
            <HubCard key={card.id} card={card} comboCodes={comboCodes} />
          ))}
        </div>
      </div>
    </section>
  );
}
